import { useState } from "react";
import { toast } from "sonner";
import { api, apiError, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import OwnerLayout from "@/components/OwnerLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Profile() {
  const { user, setUser } = useAuth();
  // The owner has no client context, so their profile sits in the owner shell.
  const Shell = user?.role === "owner" ? OwnerLayout : Layout;
  const [name, setName] = useState(user?.name || "");
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [busy, setBusy] = useState(false);

  const saveName = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/auth/profile", { name });
      setUser(data);
      toast.success("Name updated");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const savePassword = async () => {
    setBusy(true);
    try {
      await api.put("/auth/profile", pw);
      setPw({ current_password: "", new_password: "" });
      toast.success("Password changed");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell title="Your profile" subtitle={user?.email}>
      <div className="grid lg:grid-cols-2 gap-5 max-w-4xl">
        <div className="panel panel-pad">
          <div className="eyebrow">Details</div>
          <div className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="pname">Name</Label>
              <Input id="pname" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input value={user?.email || ""} disabled />
            </div>
            <div className="flex items-center justify-between text-[13px] text-muted2">
              <span>Role</span>
              <span className="capitalize text-ink font-medium">{user?.role}</span>
            </div>
            <div className="flex items-center justify-between text-[13px] text-muted2">
              <span>Joined</span>
              <span className="text-ink">{formatDate(user?.created_at)}</span>
            </div>
            <Button onClick={saveName} disabled={busy} className="bg-brand hover:bg-brand-hover">
              Save changes
            </Button>
          </div>
        </div>

        <div className="panel panel-pad">
          <div className="eyebrow">Password</div>
          <div className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="cur">Current password</Label>
              <Input
                id="cur"
                type="password"
                value={pw.current_password}
                onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new">New password</Label>
              <Input
                id="new"
                type="password"
                value={pw.new_password}
                onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
              />
              <p className="text-[12px] text-muted2">At least 8 characters.</p>
            </div>
            <Button
              onClick={savePassword}
              disabled={busy || !pw.current_password || !pw.new_password}
              className="bg-brand hover:bg-brand-hover"
            >
              Change password
            </Button>
          </div>
        </div>
      </div>
    </Shell>
  );
}
